"use client";

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../css/viewPharmacyApplication.module.css";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Popup from "../../../components/Popup";
import FormInput from "../../../components/FormInput";
import Button from "../../../components/Button";
import { approveCompanyApplication, rejectCompanyApplication } from "../../../api/admin/adminApi";

export default function ViewPharmacyApplication() {
  const location = useLocation();
  const navigate = useNavigate();
  const application = location.state?.application;
  const navigationStatus = location.state?.status;

  const [formData, setFormData] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showConfirmReject, setShowConfirmReject] = useState(false);
  const [confirmMode, setConfirmMode] = useState("reject"); // 'reject' | 'disable'

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!application) {
      // No application passed, go back to admin dashboard
      navigate("/AdminDashboard");
      return;
    }
    const director = application.director || {};
    let status;
    if (navigationStatus) {
      status = navigationStatus;
    } else if (application.isactive === true) status = "Approved";
    else if (application.isactive === false) status = "Rejected";
    else status = "Pending";

    setFormData({
      companyname: application.companyname,
      company_reg_num: application.cipcregistrationnumber,
      role: "Director",
      sapc_reg_num: application.sapcpharmacyregnumber,
      address: application.companyaddress,
      phone: application.companyphonenumber,
      name: director.firstname || "",
      surname: director.surname || "",
      email: director.email || "",
      cellnum: director.phone || "",
      status,
    });
  }, [application, navigate, navigationStatus]);

  const handleApprove = async () => {
    if (!application) return;
    try {
      setIsLoading(true);
      await approveCompanyApplication(application.cipcregistrationnumber);
      setShowSuccessPopup(true);
      setTimeout(() => {
        navigate("/AdminDashboard");
      }, 800);
    } catch (err) {
      setErrorMessage("Failed to approve application");
      setShowErrorPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = (mode = "reject") => {
    setConfirmMode(mode);
    setShowConfirmReject(true);
  };

  const confirmReject = async () => {
    if (!application) return;
    try {
      setIsLoading(true);
      await rejectCompanyApplication(application.cipcregistrationnumber);
      setShowConfirmReject(false);
      setShowSuccessPopup(true);
      setTimeout(() => {
        navigate("/AdminDashboard");
      }, 800);
    } catch (err) {
      setErrorMessage("Failed to reject application");
      setShowConfirmReject(false);
      setShowErrorPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.app}>
      <Navbar />

      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1 className={styles.title}>Application Details</h1>
          </div>

          {isLoading && <div className={styles.loading}>Processing...</div>}

          {/* Form */}
          <div className={styles.form}>
            <form className={styles.formGrid}>
              {/* Primary Application Info */}
              <FormInput label="Company Name" name="companyname" value={formData?.companyname || ""} readOnly />
              <FormInput label="CIPC Company Reg Number" name="company_reg_num" value={formData?.company_reg_num || ""} readOnly />
              <FormInput label="Role" name="role" value={formData?.role || ""} readOnly />
              <FormInput label="SAPC Pharmacy Reg Number" name="sapc_reg_num" value={formData?.sapc_reg_num || ""} readOnly />
              <FormInput label="Company Address" name="address" value={formData?.address || ""} readOnly />
              <FormInput label="Company Phone Number" name="phone" value={formData?.phone || ""} readOnly />
              <FormInput label="First Name" name="name" value={formData?.name || ""} readOnly />
              <FormInput label="Last Name" name="surname" value={formData?.surname || ""} readOnly />
              <FormInput label="Email" type="email" name="email" value={formData?.email || ""} readOnly />
              <FormInput label="Cell Number" name="cellnum" value={formData?.cellnum || ""} readOnly />
            </form>

            <div className={styles.formActions}>
              {formData?.status === "Pending" && (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={isLoading}
                    onClick={handleApprove}
                    className={styles.largeButton}
                  >
                    {isLoading ? "Processing..." : "Approve"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isLoading}
                    onClick={() => handleReject("reject")}
                    className={styles.largeButton}
                  >
                    {isLoading ? "Processing..." : "Reject"}
                  </Button>
                </>
              )}

              {formData?.status === "Approved" && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isLoading}
                  onClick={() => handleReject("disable")}
                  className={styles.largeButton}
                >
                  {isLoading ? "Processing..." : "Disable Company"}
                </Button>
              )}
            </div>
          </div>

        </div>
      </main>

      {/* Popups */}
      {showErrorPopup && (
        <Popup
          type="error"
          message={errorMessage}
          onClose={() => setShowErrorPopup(false)}
        />
      )}

      {showSuccessPopup && (
        <Popup
          type="success"
          message="Action completed successfully"
          onClose={() => setShowSuccessPopup(false)}
        />
      )}

      {showConfirmReject && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <p>
              {confirmMode === "disable"
                ? "Are you sure you want to disable this company?"
                : "Are you sure you want to reject this application?"}
            </p>

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={confirmReject}
              >
                Confirm
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowConfirmReject(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.footerWrapper}>
        <Footer />
      </div>

    </div>
  );
}